import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import ManualIndex from '../../components/Manual/ManualIndex';
import ManualSection from '../../components/Manual/ManualSection';
import SearchBar from '../../components/Manual/SearchBar';
import RoleFilter from '../../components/Manual/RoleFilter';
import {
  fetchManualIndex,
  fetchManualSection,
} from '../../api/manualService';
import './ManualPage.css';

const MANUAL_BASE_PATH = '/redcuida/doc';

const normalizeText = (value) => (value ?? '').toString().toLocaleLowerCase('es-ES');

const findFirstSlug = (items = []) => {
  for (const item of items) {
    if (item.slug) {
      return item.slug.replace(/^\/+/, '');
    }
    const childSlug = findFirstSlug(item.children || item.sections || []);
    if (childSlug) {
      return childSlug;
    }
  }
  return null;
};

const findSectionBySlug = (items = [], targetSlug) => {
  for (const item of items) {
    const itemSlug = (item.slug || item.path || '').replace(/^\/+/, '');
    if (itemSlug === targetSlug) {
      return item;
    }
    const foundChild = findSectionBySlug(item.children || item.sections || [], targetSlug);
    if (foundChild) {
      return foundChild;
    }
  }
  return null;
};

const ManualPage = ({ darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [indexData, setIndexData] = useState(null);
  const [loadingIndex, setLoadingIndex] = useState(true);
  const [indexError, setIndexError] = useState(null);

  const [sectionData, setSectionData] = useState(null);
  const [loadingSection, setLoadingSection] = useState(false);
  const [sectionError, setSectionError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');

  const slug = useMemo(() => {
    const cleanPath = location.pathname.replace(/\/+$/, '');
    if (!cleanPath.startsWith(MANUAL_BASE_PATH)) {
      return '';
    }
    const rawSlug = cleanPath.slice(MANUAL_BASE_PATH.length);
    return rawSlug.replace(/^\/+/, '');
  }, [location.pathname]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadIndex() {
      try {
        setLoadingIndex(true);
        setIndexError(null);
        const data = await fetchManualIndex(controller.signal);
        setIndexData(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setIndexError(error.message || 'No se pudo cargar el índice del manual.');
        }
      } finally {
        setLoadingIndex(false);
      }
    }

    loadIndex();

    return () => controller.abort();
  }, []);

  const sections = useMemo(() => {
    if (!indexData) {
      return [];
    }

    if (Array.isArray(indexData)) {
      return indexData;
    }

    if (Array.isArray(indexData.sections)) {
      return indexData.sections;
    }

    return [];
  }, [indexData]);

  const filteredSections = useMemo(() => {
    const applyFilter = (items) => {
      if (!items) {
        return [];
      }

      return items
        .map((item) => {
          const itemSlug = item.slug || item.path || '';
          const roles = item.roles || item.frontmatter?.roles || [];
          const tags = item.tags || item.frontmatter?.tags || [];
          const summary = item.summary || item.frontmatter?.summary || '';
          const title = item.title || '';

          const matchesRole =
            selectedRole === 'all' || roles.length === 0 || roles
              .map((role) => normalizeText(role))
              .includes(normalizeText(selectedRole));

          const matchesSearch = (() => {
            if (!searchTerm) return true;
            const term = normalizeText(searchTerm);
            return [title, summary, tags.join(' ')].some((field) =>
              normalizeText(field || '').includes(term),
            );
          })();

          const filteredChildren = applyFilter(item.children || item.sections || []);

          const keepItem = (matchesRole && matchesSearch) || filteredChildren.length > 0;

          if (!keepItem) {
            return null;
          }

          return {
            ...item,
            slug: itemSlug,
            children: filteredChildren,
          };
        })
        .filter(Boolean);
    };

    return applyFilter(sections);
  }, [sections, searchTerm, selectedRole]);

  useEffect(() => {
    if (loadingIndex) {
      return;
    }

    if (!slug) {
      const firstSlug = findFirstSlug(sections);
      if (firstSlug) {
        navigate(`${MANUAL_BASE_PATH}/${firstSlug}`, { replace: true });
      }
    }
  }, [loadingIndex, navigate, sections, slug]);

  useEffect(() => {
    if (!slug) {
      setSectionData(null);
      return;
    }

    const controller = new AbortController();

    async function loadSection() {
      try {
        setLoadingSection(true);
        setSectionError(null);
        const data = await fetchManualSection(slug, controller.signal);
        setSectionData(data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setSectionError(error.message || 'No se pudo cargar la sección solicitada.');
        }
      } finally {
        setLoadingSection(false);
      }
    }

    loadSection();

    return () => controller.abort();
  }, [slug]);

  const handleSelectSection = (sectionSlug) => {
    if (!sectionSlug) {
      return;
    }

    const normalizedSlug = sectionSlug.replace(/^\/+/, '');
    navigate(`${MANUAL_BASE_PATH}/${normalizedSlug}`);
  };

  const currentSectionMeta = useMemo(
    () => (slug ? findSectionBySlug(sections, slug) : null),
    [sections, slug],
  );

  const showFilters = sections.length > 0;

  return (
    <>
      <SEO
        title="RedCuida · Manual de uso"
        description="Consulta el manual interactivo de RedCuida: guías para la app móvil y el backoffice, con filtros por rol y recursos multimedia."
        canonical="https://e-asy.es/redcuida/doc"
        keywords="manual RedCuida, guía cuidadoras, documentación backoffice, manual app"
      />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <main className={`manual-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="manual-hero">
          <div className="manual-hero__content">
            <h1>Manual RedCuida</h1>
            <p>
              Accede a guías paso a paso, recursos y buenas prácticas para la aplicación móvil y el
              backoffice de RedCuida. Filtra por rol o busca por palabras clave para encontrar justo
              lo que necesitas.
            </p>
          </div>
        </section>

        <section className="manual-tools" aria-label="Buscador y filtros del manual">
          <SearchBar value={searchTerm} onChange={setSearchTerm} />
          {showFilters && <RoleFilter selectedRole={selectedRole} onChange={setSelectedRole} />}
        </section>

        <section className="manual-viewer">
          <aside className="manual-viewer__sidebar">
            <ManualIndex
              sections={filteredSections}
              currentSlug={slug}
              onSelect={handleSelectSection}
              isLoading={loadingIndex}
              error={indexError}
              emptyMessage={searchTerm || selectedRole !== 'all'
                ? 'No se encontraron secciones con los filtros aplicados.'
                : 'Todavía no hay secciones disponibles.'}
            />
          </aside>
          <section className="manual-viewer__content">
            <ManualSection
              section={sectionData || currentSectionMeta}
              isLoading={loadingSection}
              error={sectionError}
            />
          </section>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ManualPage;

